export type PricingRulesV1 = {
  version?: 1;
  enabled?: boolean;
  bounds?: {
    min?: number;
    max?: number;
  };
  sqm?: {
    mode?: "linear" | "tiers";
    min?: number;
    max?: number;
    step?: number;
    baseline?: number;
    scale?: number;
    clampMinMax?: boolean;
    tiers?: Array<{
      from: number;
      to: number;
      multiplier: number;
    }>;
  };
  /**
   * @deprecated The urgency multiplier is now driven exclusively by
   * `booking_policy.urgent.pricing_value`, applied automatically when the
   * server resolves `booking_mode === "urgent"`. Values here are still
   * parsed for back-compat but no longer surfaced in the vendor UI.
   */
  urgency?: {
    normal?: number;
    urgent?: number;
    same_day?: number;
  };
  types?: {
    standard?: number;
    deep_clean?: number;
    eco_friendly?: number;
    premium?: number;
    custom?: Array<{ key: string; label: string; multiplier: number; description?: string }>;
  };
};

export type PricingBounds = { min?: number; max?: number };

export function normalizePricingRules(input: any): Required<PricingRulesV1> {
  const r: PricingRulesV1 = (input && typeof input === "object") ? input : {};
  const sqm = r.sqm || {};
  const urgency = r.urgency || {};
  const types = r.types || {};
  const bounds = r.bounds || {};

  const normalizedTiers = Array.isArray(sqm.tiers)
    ? sqm.tiers
        .map((t) => ({
          from: Number((t as any).from),
          to: Number((t as any).to),
          multiplier: Number((t as any).multiplier),
        }))
        .filter((t) => Number.isFinite(t.from) && Number.isFinite(t.to) && Number.isFinite(t.multiplier) && t.to >= t.from && t.multiplier > 0)
        .sort((a, b) => a.from - b.from || a.to - b.to)
    : [];

  const mode: "linear" | "tiers" =
    (sqm.mode === "tiers" || sqm.mode === "linear")
      ? sqm.mode
      : (normalizedTiers.length > 0 ? "tiers" : "linear");

  const enabled = typeof r.enabled === "boolean" ? r.enabled : true;

  const minBound = typeof bounds.min === "number" && Number.isFinite(bounds.min) ? bounds.min : undefined;
  const maxBound = typeof bounds.max === "number" && Number.isFinite(bounds.max) ? bounds.max : undefined;

  return {
    version: 1,
    enabled,
    bounds: {
      min: minBound,
      max: maxBound,
    },
    sqm: {
      mode,
      min: typeof sqm.min === "number" ? sqm.min : 50,
      max: typeof sqm.max === "number" ? sqm.max : 500,
      step: typeof sqm.step === "number" ? sqm.step : 10,
      baseline: typeof sqm.baseline === "number" ? sqm.baseline : 100,
      scale: typeof sqm.scale === "number" ? sqm.scale : 1,
      clampMinMax: typeof sqm.clampMinMax === "boolean" ? sqm.clampMinMax : true,
      tiers: normalizedTiers,
    },
    urgency: {
      normal: typeof urgency.normal === "number" ? urgency.normal : 1,
      urgent: typeof urgency.urgent === "number" ? urgency.urgent : 1.3,
      same_day: typeof urgency.same_day === "number" ? urgency.same_day : 1.5,
    },
    types: {
      standard: typeof types.standard === "number" ? types.standard : 1,
      deep_clean: typeof types.deep_clean === "number" ? types.deep_clean : 1,
      eco_friendly: typeof types.eco_friendly === "number" ? types.eco_friendly : 1,
      premium: typeof types.premium === "number" ? types.premium : 1,
      custom: Array.isArray(types.custom) ? types.custom.filter((x) => x && x.key && x.label && typeof x.multiplier === "number") : [],
    },
  };
}

export function arePricingRulesActive(
  rules: Required<PricingRulesV1>,
  pricingType?: string | null
): boolean {
  if (pricingType === "per_sqm" || pricingType === "hourly") return true;
  return rules.enabled !== false;
}

export function applyBounds(total: number, bounds: PricingBounds | undefined): number {
  let v = Math.max(0, Math.round(Number(total) || 0));
  if (bounds && typeof bounds.min === "number" && Number.isFinite(bounds.min)) {
    v = Math.max(v, Math.round(bounds.min));
  }
  if (bounds && typeof bounds.max === "number" && Number.isFinite(bounds.max)) {
    v = Math.min(v, Math.round(bounds.max));
  }
  return v;
}

export function percentToMultiplier(pct: number): number {
  const n = Number(pct);
  if (!Number.isFinite(n)) return 1;
  return 1 + n / 100;
}

export function multiplierToPercent(mult: number): number {
  const n = Number(mult);
  if (!Number.isFinite(n)) return 0;
  return Math.round((n - 1) * 100);
}

export function computeSizeMultiplier(sqmValue: number, rules: Required<PricingRulesV1>["sqm"]): number {
  const baseline = Math.max(1, Number(rules.baseline || 100));
  const scale = Number(rules.scale || 1);
  let sqm = Number(sqmValue || baseline);
  if (rules.clampMinMax) {
    sqm = Math.max(Number(rules.min || 0), Math.min(Number(rules.max || sqm), sqm));
  }
  if (rules.mode === "tiers" && Array.isArray(rules.tiers) && rules.tiers.length > 0) {
    const tiers = rules.tiers
      .map((t) => ({ from: Number(t.from), to: Number(t.to), multiplier: Number(t.multiplier) }))
      .filter((t) => Number.isFinite(t.from) && Number.isFinite(t.to) && Number.isFinite(t.multiplier) && t.to >= t.from && t.multiplier > 0)
      .sort((a, b) => a.from - b.from || a.to - b.to);

    const hit = tiers.find((t) => sqm >= t.from && sqm <= t.to);
    if (hit) return hit.multiplier;

    if (tiers.length > 0) {
      if (sqm < tiers[0].from) return tiers[0].multiplier;
      if (sqm > tiers[tiers.length - 1].to) return tiers[tiers.length - 1].multiplier;
      const below = [...tiers].reverse().find((t) => t.to < sqm);
      if (below) return below.multiplier;
      const above = tiers.find((t) => t.from > sqm);
      if (above) return above.multiplier;
    }
    return 1;
  }
  const raw = 1 + ((sqm - baseline) / baseline) * scale;
  return Math.max(0.1, raw);
}

export function getTypeMultiplier(selectedTypeKey: string, rules: Required<PricingRulesV1>["types"]): number {
  const key = String(selectedTypeKey || "").trim();
  if (!key) return 1;
  const base = (rules as any)[key];
  if (typeof base === "number") return base;
  const custom = (rules.custom || []).find((x) => x.key === key);
  return typeof custom?.multiplier === "number" ? custom.multiplier : 1;
}

export function computeServiceTotal(args: {
  basePrice: number;
  rules: Required<PricingRulesV1>;
  pricingType?: string | null;
  sqm?: number;
  urgencyMultiplier?: number;
  serviceTypeKey?: string;
}): {
  total: number;
  rawTotal: number;
  sizeMultiplier: number;
  urgencyMultiplier: number;
  typeMultiplier: number;
  clamped: "min" | "max" | null;
} {
  const base = Math.max(0, Number(args.basePrice) || 0);
  const active = arePricingRulesActive(args.rules, args.pricingType);
  if (!active) {
    return {
      total: Math.round(base),
      rawTotal: Math.round(base),
      sizeMultiplier: 1,
      urgencyMultiplier: 1,
      typeMultiplier: 1,
      clamped: null,
    };
  }
  const sizeMult = typeof args.sqm === "number"
    ? computeSizeMultiplier(args.sqm, args.rules.sqm)
    : 1;
  const urgMult = typeof args.urgencyMultiplier === "number" && Number.isFinite(args.urgencyMultiplier)
    ? args.urgencyMultiplier
    : 1;
  const typeMult = args.serviceTypeKey
    ? getTypeMultiplier(args.serviceTypeKey, args.rules.types)
    : 1;
  // Urgency must apply AFTER bounds, so the +50% surcharge is always
  // visible on top of the clamped service line (otherwise per-sqm
  // pricing whose raw value gets lifted to bounds.min eats the
  // urgency multiplier entirely — see CF19).
  const rawWithoutUrgency = Math.round(base * sizeMult * typeMult);
  const boundedWithoutUrgency = applyBounds(rawWithoutUrgency, args.rules.bounds);
  let clamped: "min" | "max" | null = null;
  if (typeof args.rules.bounds?.min === "number" && rawWithoutUrgency < args.rules.bounds.min) clamped = "min";
  else if (typeof args.rules.bounds?.max === "number" && rawWithoutUrgency > args.rules.bounds.max) clamped = "max";
  const total = Math.round(boundedWithoutUrgency * urgMult);
  return {
    total,
    rawTotal: Math.round(base * sizeMult * urgMult * typeMult),
    sizeMultiplier: sizeMult,
    urgencyMultiplier: urgMult,
    typeMultiplier: typeMult,
    clamped,
  };
}
